'use client';

import { useState, useEffect } from 'react';
import {
    Search, Globe, ShieldCheck, Zap, ArrowRight, CheckCircle2,
    Crown, ExternalLink, KeyRound, Smartphone, Heart, HelpCircle
} from 'lucide-react';
import Link from 'next/link';

export default function LandingPage() {
    const [queryDomain, setQueryDomain] = useState('');
    const [queryResult, setQueryResult] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [prices, setPrices] = useState({ ANNUAL: 999, PERMANENT: 3999 });

    useEffect(() => {
        fetch('/api/public/configs/prices')
            .then(res => res.json())
            .then(data => setPrices(data))
            .catch(() => { });
    }, []);

    const handleQuery = async () => {
        if (!queryDomain) return;
        setLoading(true);
        try {
            const res = await fetch(`/api/public/license/query?domain=${queryDomain}`);
            const data = await res.json();
            setQueryResult(data);
        } catch (error) {
            setQueryResult({ error: '查询失败' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 font-sans selection:bg-primary-100 selection:text-primary-700">
            {/* Header */}
            <header className="fixed top-0 w-full z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/50">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2 group cursor-pointer">
                        <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center text-white shadow-lg transition-transform group-hover:rotate-12">
                            <ShieldCheck className="w-5 h-5" />
                        </div>
                        <span className="font-bold text-lg text-slate-900 tracking-tight">全域魔力 <span className="text-primary-600">授权中心</span></span>
                    </div>
                    <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
                        <Link href="#query" className="hover:text-primary-600 transition-colors">授权查询</Link>
                        <Link href="#pricing" className="hover:text-primary-600 transition-colors">购买授权</Link>
                    </nav>
                    <div className="flex items-center gap-4">
                        <Link href="#pricing" className="text-sm font-semibold text-white bg-slate-900 px-6 py-2 rounded-full hover:bg-slate-800 transition-all shadow-md active:scale-95">
                            立即购买
                        </Link>
                    </div>
                </div>
            </header>

            <main className="pt-24">
                {/* Hero Section */}
                <section className="px-6 pb-20 pt-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-bold mb-6 animate-bounce">
                            <Zap className="w-3 h-3 fill-primary-600" />
                            企业级智能建站新标准
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black text-slate-900 tracking-tight mb-8 leading-[1.1]">
                            为数字化转型提供 <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-indigo-600">更智能的驱动力</span>
                        </h1>
                        <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed mb-10">
                            GeoCMS 集成先进的 AI 创作与搜索引擎推送技术，助力企业快速构建高排名的现代化官网。购买正版授权，享受全方位技术支持与持续自动更新。
                        </p>

                        {/* Query Box */}
                        <div id="query" className="max-w-2xl mx-auto p-2 bg-white rounded-2xl shadow-2xl shadow-primary-200/50 border border-slate-100 flex items-center transition-all focus-within:ring-4 focus-within:ring-primary-100">
                            <div className="pl-4 pr-2 text-slate-400">
                                <Globe className="w-5 h-5" />
                            </div>
                            <input
                                type="text"
                                placeholder="输入域名查询授权状态 (如: example.com)"
                                className="flex-1 bg-transparent border-none focus:ring-0 text-slate-900 font-medium placeholder:text-slate-300 h-10"
                                value={queryDomain}
                                onChange={(e) => setQueryDomain(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleQuery()}
                            />
                            <button
                                onClick={handleQuery}
                                className="h-12 px-8 bg-primary-600 text-white rounded-xl font-bold flex items-center gap-2 hover:bg-primary-700 transition-all active:scale-95 shadow-lg shadow-primary-600/20"
                            >
                                <Search className="w-4 h-4" />
                                查询授权
                            </button>
                        </div>

                        {/* Query Result UI */}
                        {queryResult && (
                            <div className="mt-8 animate-in fade-in slide-in-from-top-4 duration-500">
                                {queryResult.isLicensed ? (
                                    <div className="inline-flex items-center gap-6 p-4 bg-green-50 border border-green-100 rounded-2xl text-left">
                                        <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center text-white shadow-lg">
                                            <CheckCircle2 className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <div className="font-bold text-green-800">该域名已获得正版授权</div>
                                            <div className="text-sm text-green-600">授权到期: {new Date(queryResult.expiresAt).toLocaleDateString()} · 版本: {queryResult.plan}</div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="inline-flex items-center gap-6 p-4 bg-amber-50 border border-amber-100 rounded-2xl text-left">
                                        <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center text-white shadow-lg">
                                            <HelpCircle className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <div className="font-bold text-amber-800">该域名尚未获得授权</div>
                                            <div className="text-sm text-amber-600">探测到该域名可能正在运行 GeoCMS，请及时购买以获得商业支持。</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </section>

                {/* Trust Section */}
                <section className="py-20 bg-white border-y border-slate-100">
                    <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-12 text-center">
                        <FeatureItem icon={Crown} title="商业合法性" desc="获得正版软件使用权，解除法律侵权风险" />
                        <FeatureItem icon={Zap} title="一键自动化" desc="AI 智能创作与全自动 SEO 优化技术" />
                        <FeatureItem icon={Smartphone} title="多端适配" desc="响应式布局，支持 PC、平板及手机访问" />
                        <FeatureItem icon={Heart} title="优先支持" desc="专业工程师提供一对一集成与运维指导" />
                    </div>
                </section>

                {/* Pricing Section */}
                <section id="pricing" className="py-32 px-6">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-20">
                            <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mb-4">透明定价，助力增长</h2>
                            <p className="text-slate-500">无隐藏费用，自助下单即刻发放授权码</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                            <PricingCard
                                plan="年度订阅"
                                price={`¥ ${prices.ANNUAL}`}
                                unit="/ 年"
                                desc="适合正在成长的初创企业"
                                features={['核心建站功能', '常规更新维护', '1年社区支持', '基础 AI 模型']}
                                highlight={false}
                                href="/buy?plan=ANNUAL"
                            />
                            <PricingCard
                                plan="终身授权"
                                price={`¥ ${prices.PERMANENT}`}
                                unit="/ 一次性"
                                desc="专业团队及大规模建站首选"
                                features={['所有高级特权', '永久系统更新', '优先专家席位', '全量 AI 模型插件', '无限制推送额度']}
                                highlight={true}
                                href="/buy?plan=PERMANENT"
                            />
                        </div>
                    </div>
                </section>
            </main>

            <footer className="py-20 text-center text-slate-400 text-sm border-t border-slate-200/50">
                <p>© 2026 全域魔力 (GeoCMS) · 正版系统授权中心</p>
            </footer>
        </div>
    );
}

function FeatureItem({ icon: Icon, title, desc }: any) {
    return (
        <div className="group">
            <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 mb-6 mx-auto group-hover:bg-primary-50 group-hover:text-primary-600 transition-all duration-300">
                <Icon className="w-8 h-8" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">{title}</h3>
            <p className="text-sm text-slate-500">{desc}</p>
        </div>
    );
}

function PricingCard({ plan, price, unit, desc, features, highlight, href }: any) {
    return (
        <div className={`p-10 rounded-[2.5rem] border transition-all duration-300 flex flex-col ${highlight
            ? 'bg-slate-900 border-slate-800 text-white shadow-2xl shadow-primary-900/10 scale-105 z-10'
            : 'bg-white border-slate-200 text-slate-900 hover:border-primary-200 hover:shadow-xl hover:shadow-primary-100/50'
            }`}>
            <div className="mb-10">
                <h3 className="text-xl font-bold mb-2">{plan}</h3>
                <p className={`text-sm ${highlight ? 'text-slate-400' : 'text-slate-500'}`}>{desc}</p>
            </div>
            <div className="flex items-baseline gap-2 mb-10">
                <span className="text-4xl font-black">{price}</span>
                <span className={`text-sm ${highlight ? 'text-slate-400' : 'text-slate-500'}`}>{unit}</span>
            </div>
            <div className="space-y-4 mb-10 flex-1">
                {features.map((f: string) => (
                    <div key={f} className="flex items-center gap-3">
                        <CheckCircle2 className={`w-4 h-4 ${highlight ? 'text-primary-400' : 'text-primary-600'}`} />
                        <span className="text-sm font-medium">{f}</span>
                    </div>
                ))}
            </div>
            <Link
                href={href}
                className={`w-full py-4 rounded-2xl font-bold text-center transition-all active:scale-95 ${highlight
                    ? 'bg-white text-slate-900 hover:bg-slate-50'
                    : 'bg-primary-600 text-white hover:bg-primary-700 shadow-lg shadow-primary-600/20'
                    }`}
            >
                立即配置
            </Link>
        </div>
    );
}

function RefreshCw({ className }: any) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
            <path d="M21 3v5h-5" />
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
            <path d="M3 21v-5h5" />
        </svg>
    );
}
